import React, { useState, useEffect } from 'react';
import {
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  Switch,
  FormControlLabel,
  Tooltip,
  TextField,
  InputAdornment,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
  Group as GroupIcon,
  Psychology as PsychologyIcon,
  List as ListIcon,
  AdminPanelSettings as AdminIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ROUTES, ordreNiveaux, cyclePositionIndex } from '../../config/shared_config';
import AdminLayout from '../../layouts/AdminLayout';
import { AdminService } from '../../services/adminService';
import UsageCreditsCard from '../../components/admin/UsageCreditsCard';
import SubscriptionGrantCard from '../../components/admin/SubscriptionGrantCard';
import { useAuth } from '../../contexts/AuthContext';
import { ADMIN_ROLE_LABELS, ADMIN_ROLES } from '../../config/adminRoles';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, ReferenceLine, Cell, Legend
} from 'recharts';

// Échelle et mapping cycle→index viennent de la source de vérité partagée.
// Ils étaient recopiés ici, tronqués à 6ème et avec un troisième mapping encore
// différent ({2:2, 3:3, 4:5}) — un enfant de cycle 4, désormais amorcé à
// l'index 7 (4ème), sortait du graphe et s'affichait « undefined ».
const ORDRE_NIVEAUX = ordreNiveaux;
const INDEX_MAX = ORDRE_NIVEAUX.length - 1;
const AXIS_TICKS = ORDRE_NIVEAUX.map((_, i) => i);
const DOMAIN_LABELS = {
  grammaire: 'Grammaire', orthographe: 'Orthographe',
  vocabulaire: 'Vocabulaire', comprehension: 'Compréhension', ponctuation: 'Ponctuation',
};
const cycleToIndex = (cycle) => cyclePositionIndex(cycle);

// Profils de compte (axe B). Les clés sont celles calculées par le backend
// (`ACCOUNT_PROFILE_SQL` dans models/admin.queries.js), lui-même aligné sur la
// règle du résolveur d'entitlements — la console doit classer un compte comme
// l'app l'applique.
const ACCOUNT_PROFILES = {
  enfant: { label: 'Enfant', color: 'secondary', aide: 'Mineur supervisé : moins de 15 ans, compte non-parent' },
  anonyme: { label: 'Anonyme', color: 'default', aide: "Invité d'onboarding : connexion anonyme, âge inconnu ou 15+" },
  authentifie: { label: 'Authentifié', color: 'success', aide: 'Compte adulte authentifié (email ou OAuth)' },
  autre: { label: 'Autre', color: 'warning', aide: "Niveau d'authentification absent ou inconnu" },
  bot: { label: 'Bot', color: 'default', aide: 'Compte de test interne (@dictoquest.internal)' },
};

export default function UserListScreen() {
  const navigate = useNavigate();
  const { isFullAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    isActive: null,
    isParentAccount: null,
    includeBots: false,
    accountProfile: null,
  });
  const [totalUsers, setTotalUsers] = useState(0);
  const [profileCounts, setProfileCounts] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailsOpen, setUserDetailsOpen] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage, searchTerm, filters]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { users: usersData, total, profileCounts: counts } = await AdminService.getUsers({
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm,
        isActive: filters.isActive,
        isParentAccount: filters.isParentAccount,
        includeBots: filters.includeBots,
        accountProfile: filters.accountProfile,
      });
      setUsers(usersData || []);
      setTotalUsers(total || 0);
      setProfileCounts(counts || {});
      setLoading(false);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Impossible de charger les utilisateurs');
      setLoading(false);
    }
  };

  const handleViewUserDetails = async (user) => {
    setSelectedUser(user);
    setUserDetailsOpen(true);
    setLoadingDetails(true);
    
    try {
      const [userDetails, combatStats, competences, levelEstimate, navigationHistory] = await Promise.all([
        AdminService.getUserById(user.uid),
        AdminService.getUserCombatStats(user.uid),
        AdminService.getUserCompetences(user.uid),
        AdminService.getUserLevelEstimate(user.uid).catch(() => null),
        AdminService.getUserNavigationHistory(user.uid, 1, 50).catch(() => ({ history: [] })),
      ]);

      setUserDetails({
        ...userDetails,
        combatStats,
        competences,
        levelEstimate,
        navigationHistory: navigationHistory.history || [],
      });
    } catch (err) {
      console.error('Error fetching user details:', err);
      setError('Impossible de charger les détails de l\'utilisateur');
    } finally {
      setLoadingDetails(false);
    }
  };

  /** Après un abonnement offert : seul le bloc abonnement change, on ne recharge que la fiche. */
  const refreshUserSubscription = async (uid) => {
    try {
      const fresh = await AdminService.getUserById(uid);
      setUserDetails((prev) => (prev ? { ...prev, ...fresh } : prev));
      fetchUsers();
    } catch (err) {
      console.error('Error refreshing user details:', err);
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      await AdminService.toggleUserStatus(userId);
      // Actualiser la liste
      fetchUsers();
    } catch (err) {
      console.error('Error toggling user status:', err);
      setError('Impossible de modifier le statut de l\'utilisateur');
    }
  };

  const handleSetAdminRole = async (userId, role) => {
    try {
      const result = await AdminService.setUserAdminRole(userId, role);
      if (result.success) {
        fetchUsers();
      }
    } catch (err) {
      console.error('Error setting admin role:', err);
      setError('Impossible de modifier le rôle administrateur');
    }
  };

  const getAdminRoleLabel = (user) => {
    if (!user.isAdmin && !user.adminRole) return 'Utilisateur';
    return ADMIN_ROLE_LABELS[user.adminRole] || 'Administrateur';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Jamais';
    return new Date(dateString).toLocaleString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const PLAN_COLORS = {
    free: 'default',
    premium: 'primary',
    premium_plus: 'secondary',
  };

  const SOURCE_LABELS = {
    stripe: 'Stripe',
    apple: 'Apple',
    google: 'Google',
    apple_family_sharing: 'Apple Family Sharing',
    google_family_sharing: 'Google Family Sharing',
    admin: 'Admin / cadeau',
    gift_beta: 'Cadeau bêta',
    test_payment: 'Test',
    own: 'Propre',
    parent_delegation: 'Délégation parent',
    family_pool: 'Pool familial',
    family_sharing: 'Family Sharing',
    direct_family_link: 'Lien famille direct',
  };

  const STATUS_LABELS = {
    active: 'Actif',
    expired: 'Expiré',
    cancelled: 'Annulé',
    pending: 'En attente',
    completed: 'Payé',
    failed: 'Échoué',
    refunded: 'Remboursé',
  };

  const planChipColor = (plan) => PLAN_COLORS[plan] || 'default';
  const sourceLabel = (source) => SOURCE_LABELS[source] || source || '—';
  const statusLabel = (status) => STATUS_LABELS[status] || status || '—';

  const planLabel = (plan, isTrial) =>
    isTrial ? 'trial' : plan === 'premium_plus' ? 'premium+' : plan;

  /**
   * Plan EFFECTIF — celui que l'app applique, renvoyé par le résolveur
   * d'entitlements (champs `effective*`). Il diffère du plan propre dès
   * que les droits viennent d'ailleurs : enfant couvert par le pool familial,
   * délégation parent, Family Sharing. On affiche l'effectif en principal, et
   * on ne rappelle le plan propre que lorsqu'ils divergent — sinon la ligne
   * dirait `free` d'un enfant qui joue en premium.
   */
  const renderPlanChip = (user) => {
    const own = user.planType || 'free';
    const resolu = !!user.effectivePlanType;
    const plan = resolu ? user.effectivePlanType : own;
    const isTrial = resolu
      ? !!user.effectiveIsTrialing
      : (user.trialEndDate && new Date(user.trialEndDate) > new Date()
         && (own === 'free' || user.subscriptionStatus === 'active'));
    const finAbo = resolu ? user.effectiveSubscriptionEndsAt : user.subscriptionEndDate;
    const viaAutrui = resolu && user.effectiveEntitledBy && user.effectiveEntitledBy !== 'self';
    const divergent = resolu && user.effectivePlanType !== own;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Chip
          label={planLabel(plan, isTrial)}
          color={planChipColor(isTrial && plan === 'free' ? 'premium' : plan)}
          size="small"
        />
        {viaAutrui && (
          <Tooltip title={`Droits hérités — ${sourceLabel(user.effectiveBillingSource)}`}>
            <Typography variant="caption" color="text.secondary">
              {/* Le contributeur n'est nommé que lorsque le pool familial l'a
                  désigné ; sinon on se rabat sur la nature du lien, sans « via »
                  qui donnerait « via Délégation parent ». */}
              {user.effectiveContributedBy?.displayName
                ? `via ${user.effectiveContributedBy.displayName}`
                : sourceLabel(user.effectiveBillingSource)}
            </Typography>
          </Tooltip>
        )}
        {divergent && (
          <Tooltip title="Abonnement propre au compte, hors héritage familial">
            <Typography variant="caption" color="text.disabled">
              propre : {own}
            </Typography>
          </Tooltip>
        )}
        {user.subscriptionSource && own !== 'free' && (
          <Typography variant="caption" color="text.secondary">
            {sourceLabel(user.subscriptionSource)}
          </Typography>
        )}
        {finAbo && plan !== 'free' && (
          <Typography variant="caption" color="text.secondary">
            → {formatDateShort(finAbo)}
          </Typography>
        )}
        {user.effectiveParentalConsent === 'pending' && (
          <Tooltip title="Consentement parental en attente : les droits payants sont bloqués">
            <Chip label="consentement" color="warning" size="small" variant="outlined" />
          </Tooltip>
        )}
      </Box>
    );
  };

  const UserDetailsDialog = () => (
    <Dialog 
      open={userDetailsOpen} 
      onClose={() => setUserDetailsOpen(false)}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar src={selectedUser?.avatar} alt={selectedUser?.displayName}>
            {selectedUser?.displayName?.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="h6">{selectedUser?.displayName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedUser?.email}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {loadingDetails ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : userDetails ? (
          <Grid container spacing={3}>
            {/* Informations générales */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Informations générales" />
                <CardContent>
                  <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={userDetails.isParentAccount ? "Compte parent" : "Compte enfant / joueur"}
                      color={userDetails.isParentAccount ? "info" : "default"}
                      size="small"
                      icon={userDetails.isParentAccount ? <GroupIcon /> : <PersonIcon />}
                    />
                    <Chip
                      label={userDetails.accountStatus === 'active' ? "Actif" :
                             userDetails.accountStatus === 'pending_validation' ? "En attente validation" :
                             userDetails.accountStatus === 'suspended' ? "Suspendu" : userDetails.accountStatus}
                      color={userDetails.accountStatus === 'active' ? "success" :
                             userDetails.accountStatus === 'pending_validation' ? "warning" : "error"}
                      size="small"
                    />
                    {userDetails.adminRole && (
                      <Chip
                        label={ADMIN_ROLE_LABELS[userDetails.adminRole] || userDetails.adminRole}
                        color={userDetails.adminRole === ADMIN_ROLES.ADMIN ? 'warning' : 'info'}
                        size="small"
                        icon={<AdminIcon />}
                      />
                    )}
                    <Chip
                      label={userDetails.onboardingCompleted ? "Onboarding terminé" : "Onboarding en cours"}
                      color={userDetails.onboardingCompleted ? "success" : "warning"}
                      size="small"
                      variant={userDetails.onboardingCompleted ? "default" : "outlined"}
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Cycle scolaire</Typography>
                    <Typography variant="body1">{userDetails.cycle ? `Cycle ${userDetails.cycle}` : 'N/A'}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Niveau / XP</Typography>
                    <Typography variant="body1">{userDetails.level} — {userDetails.xp} XP</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Pièces</Typography>
                    <Typography variant="body1">{userDetails.coins}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Série de connexion</Typography>
                    <Typography variant="body1">{userDetails.currentStreak} jours (max : {userDetails.maxStreak})</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Dernière connexion</Typography>
                    <Typography variant="body1">{formatDate(userDetails.lastLoginDate)}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Statistiques de combats */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Statistiques de combats" />
                <CardContent>
                  {userDetails.isParentAccount ? (
                    <Typography variant="body2" color="text.secondary">
                      Compte parent — pas de combats joués directement.
                    </Typography>
                  ) : (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Combats joués</Typography>
                        <Typography variant="body1">{userDetails.combatStats?.totalCombats || 0}</Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Victoires / Défaites</Typography>
                        <Typography variant="body1">
                          {userDetails.combatStats?.combatsWon || 0} V — {userDetails.combatStats?.combatsLost || 0} D
                          {userDetails.combatStats?.winRate != null && ` (${userDetails.combatStats.winRate}%)`}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">XP total gagné</Typography>
                        <Typography variant="body1">{userDetails.combatStats?.totalXpEarned || 0}</Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Dernier combat</Typography>
                        <Typography variant="body1">{formatDate(userDetails.combatStats?.lastCombatDate)}</Typography>
                      </Box>
                      {userDetails.combatStats?.byContext?.length > 0 && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Par contexte</Typography>
                          {userDetails.combatStats.byContext.map((ctx, i) => (
                            <Chip key={i} label={`${ctx.contexte} : ${ctx.combatsCount}`} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                          ))}
                        </Box>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Abonnement & paiements */}
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Abonnement & paiements" />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">Plan en base</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Chip
                          label={userDetails.planType || 'free'}
                          color={planChipColor(userDetails.planType || 'free')}
                          size="small"
                        />
                        {userDetails.subscriptionStatus && (
                          <Chip
                            label={statusLabel(userDetails.subscriptionStatus)}
                            color={userDetails.subscriptionStatus === 'active' ? 'success' : 'default'}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">Plan effectif</Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                        {userDetails.effectivePlanType || userDetails.planType || 'free'}
                        {userDetails.effectiveIsTrialing ? ' (trial)' : ''}
                      </Typography>
                      {userDetails.effectiveBillingSource && (
                        <Typography variant="caption" color="text.secondary">
                          via {sourceLabel(userDetails.effectiveBillingSource)}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">Source / plateforme</Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                        {sourceLabel(userDetails.subscriptionSource)}
                      </Typography>
                      {userDetails.entitlementSource && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          entitlement_source : {userDetails.entitlementSource}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">Fin d&apos;abonnement</Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                        {formatDate(userDetails.effectiveSubscriptionEndsAt || userDetails.subscriptionEndDate)}
                      </Typography>
                      {userDetails.trialEndDate && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Trial jusqu&apos;au {formatDate(userDetails.trialEndDate)}
                          {userDetails.trialPlanType ? ` (${userDetails.trialPlanType})` : ''}
                        </Typography>
                      )}
                    </Grid>
                    {userDetails.subscriptionPaymentId && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">ID paiement / abo</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                          {userDetails.subscriptionPaymentId}
                        </Typography>
                      </Grid>
                    )}
                    {userDetails.stripeCustomerId && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Stripe customer</Typography>
                        <Typography
                          component="a"
                          href={`https://dashboard.stripe.com/customers/${userDetails.stripeCustomerId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                          sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                        >
                          {userDetails.stripeCustomerId}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>

                  <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                    Dernières transactions
                  </Typography>
                  {userDetails.paymentTransactions?.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Plateforme</TableCell>
                            <TableCell>Produit</TableCell>
                            <TableCell>Montant</TableCell>
                            <TableCell>Statut</TableCell>
                            <TableCell>ID transaction</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {userDetails.paymentTransactions.map((tx) => (
                            <TableRow key={tx.id}>
                              <TableCell>{formatDate(tx.createdAt)}</TableCell>
                              <TableCell>{sourceLabel(tx.platform)}</TableCell>
                              <TableCell>{tx.productName || tx.productId || '—'}</TableCell>
                              <TableCell>
                                {tx.amount != null
                                  ? `${Number(tx.amount).toFixed(2)} ${tx.currency || 'EUR'}`
                                  : '—'}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={statusLabel(tx.status)}
                                  size="small"
                                  color={
                                    tx.status === 'completed' ? 'success'
                                      : tx.status === 'refunded' || tx.status === 'failed' ? 'error'
                                        : 'default'
                                  }
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                  {tx.platformTransactionId}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Aucune transaction enregistrée
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Abonnement offert — Aventurier / Champion pour une durée donnée */}
            <Grid item xs={12} md={6}>
              <SubscriptionGrantCard
                userId={selectedUser?.uid}
                onGranted={() => refreshUserSubscription(selectedUser?.uid)}
              />
            </Grid>

            {/* Crédits d'usage — plancher scan / Sage + octroi manuel (P5 / D-11) */}
            <Grid item xs={12} md={6}>
              <UsageCreditsCard userId={selectedUser?.uid} />
            </Grid>

            {/* Guilde */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Guilde" />
                <CardContent>
                  {userDetails.guildName ? (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Nom de la guilde</Typography>
                        <Typography variant="body1">{userDetails.guildName}</Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Rang</Typography>
                        <Typography variant="body1">{userDetails.guildRank}</Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Points de guilde</Typography>
                        <Typography variant="body1">{userDetails.guildPoints}</Typography>
                      </Box>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Aucune guilde
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Compétences */}
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Compétences et niveaux de maîtrise" />
                <CardContent>
                  {userDetails.competences?.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Domaine</TableCell>
                            <TableCell>Compétence</TableCell>
                            <TableCell>Notion</TableCell>
                            <TableCell>Maîtrise</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {userDetails.competences.map((competence, index) => (
                            <TableRow key={index}>
                              <TableCell>{competence.domain}</TableCell>
                              <TableCell>{competence.competence}</TableCell>
                              <TableCell>{competence.notion}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={`${competence.mastery}%`}
                                  color={
                                    competence.mastery >= 80 ? 'success' :
                                    competence.mastery >= 60 ? 'warning' : 'error'
                                  }
                                  size="small"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Aucune compétence enregistrée
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            {/* Niveau adaptatif estimé */}
            {userDetails.levelEstimate?.estimates?.length > 0 && (() => {
              const declaredIdx = cycleToIndex(userDetails.levelEstimate.declaredCycle);
              const chartData = userDetails.levelEstimate.estimates.map(e => ({
                domain: DOMAIN_LABELS[e.domain] || e.domain,
                domainKey: e.domain,
                estimé: parseFloat(e.estimatedIndex),
                confiance: Math.round(parseFloat(e.confidence) * 100),
                combats: e.combatsCount,
                combatsDepuisIA: e.combatsSinceAi,
                derniereIA: e.lastAiUpdate
                  ? new Date(e.lastAiUpdate).toLocaleDateString('fr-FR')
                  : '–',
                misAJour: e.updatedAt
                  ? new Date(e.updatedAt).toLocaleDateString('fr-FR')
                  : '–',
              }));

              const CustomTooltip = ({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0]?.payload;
                return (
                  <Box sx={{ bgcolor: 'background.paper', border: '1px solid #ddd', p: 1.5, borderRadius: 1 }}>
                    <Typography variant="subtitle2">{label}</Typography>
                    <Typography variant="body2">Niveau estimé : <strong>{ORDRE_NIVEAUX[Math.round(d.estimé)] || d.estimé.toFixed(1)}</strong> ({d.estimé.toFixed(2)})</Typography>
                    <Typography variant="body2">Niveau déclaré : <strong>{ORDRE_NIVEAUX[declaredIdx]}</strong></Typography>
                    <Typography variant="body2">Confiance : <strong>{d.confiance}%</strong></Typography>
                    <Typography variant="body2">Combats : {d.combats} (depuis IA : {d.combatsDepuisIA}/10)</Typography>
                    <Typography variant="body2">Dernière analyse IA : {d.derniereIA}</Typography>
                    <Typography variant="body2">Mis à jour : {d.misAJour}</Typography>
                  </Box>
                );
              };

              return (
                <Grid item xs={12}>
                  <Card>
                    <CardHeader
                      title="Niveau adaptatif estimé par domaine"
                      subheader={`Cycle déclaré : ${ORDRE_NIVEAUX[declaredIdx]} (index ${declaredIdx}) — la ligne pointillée indique le niveau attendu`}
                    />
                    <CardContent>
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="domain" />
                          <YAxis domain={[0, INDEX_MAX]} ticks={AXIS_TICKS} tickFormatter={i => ORDRE_NIVEAUX[i] || i} />
                          <RechartsTooltip content={<CustomTooltip />} />
                          <Legend formatter={v => v === 'estimé' ? 'Niveau estimé' : 'Confiance (×5)'} />
                          <ReferenceLine y={declaredIdx} stroke="#ff7043" strokeDasharray="5 3" label={{ value: 'Déclaré', position: 'right', fontSize: 11, fill: '#ff7043' }} />
                          <Bar dataKey="estimé" name="estimé" radius={[4,4,0,0]}>
                            {chartData.map((entry, i) => (
                              <Cell key={i} fill={
                                entry.estimé >= declaredIdx + 0.5 ? '#4caf50' :
                                entry.estimé <= declaredIdx - 0.5 ? '#f44336' : '#2196f3'
                              } fillOpacity={0.4 + entry.confiance / 170} />
                            ))}
                          </Bar>
                          <Bar dataKey={d => d.confiance / 20} name="confiance" fill="#9c27b0" fillOpacity={0.35} radius={[4,4,0,0]} />
                        </BarChart>
                      </ResponsiveContainer>
                      <TableContainer sx={{ mt: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Domaine</TableCell>
                              <TableCell>Niveau estimé</TableCell>
                              <TableCell>vs déclaré</TableCell>
                              <TableCell>Confiance</TableCell>
                              <TableCell>Combats</TableCell>
                              <TableCell>Depuis IA</TableCell>
                              <TableCell>Dernière IA</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {chartData.map((row, i) => {
                              const delta = row.estimé - declaredIdx;
                              return (
                                <TableRow key={i}>
                                  <TableCell>{row.domain}</TableCell>
                                  <TableCell><strong>{ORDRE_NIVEAUX[Math.round(row.estimé)]}</strong> <Typography component="span" variant="caption" color="text.secondary">({row.estimé.toFixed(2)})</Typography></TableCell>
                                  <TableCell>
                                    <Chip size="small"
                                      label={delta > 0 ? `+${delta.toFixed(1)}` : delta.toFixed(1)}
                                      color={delta >= 0.5 ? 'success' : delta <= -0.5 ? 'error' : 'default'}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box sx={{ flex: 1, bgcolor: '#e0e0e0', borderRadius: 1, height: 6 }}>
                                        <Box sx={{ width: `${row.confiance}%`, bgcolor: row.confiance > 60 ? '#4caf50' : row.confiance > 30 ? '#ff9800' : '#f44336', height: '100%', borderRadius: 1 }} />
                                      </Box>
                                      <Typography variant="caption">{row.confiance}%</Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>{row.combats}</TableCell>
                                  <TableCell>
                                    <Chip size="small" label={`${row.combatsDepuisIA}/10`}
                                      color={row.combatsDepuisIA >= 8 ? 'warning' : 'default'} />
                                  </TableCell>
                                  <TableCell><Typography variant="caption">{row.derniereIA}</Typography></TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })()}

            {/* Historique de navigation */}
            <Grid item xs={12}>
              <Card>
                <CardHeader 
                  title="Historique de navigation récent" 
                  avatar={<TimelineIcon />}
                  action={
                    <Button 
                      size="small" 
                      onClick={() => navigate(`/admin/users/${selectedUser.uid}/history`)}
                    >
                      Voir tout l'historique
                    </Button>
                  }
                />
                <CardContent>
                  {userDetails.navigationHistory?.length > 0 ? (
                    <TableContainer sx={{ maxHeight: 400, border: '1px solid rgba(224, 224, 224, 1)', borderRadius: 1 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 'bold' }}>Date / Heure</TableCell>
                            <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 'bold' }}>Route / Écran</TableCell>
                            <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 'bold' }}>Session</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {userDetails.navigationHistory.map((entry, index) => (
                            <TableRow key={index} hover>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(entry.createdAt)}</TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Chip 
                                    label={entry.routePath} 
                                    size="small" 
                                    color="primary"
                                    variant="outlined"
                                    sx={{ 
                                      height: '20px', 
                                      fontSize: '0.65rem',
                                      width: 'fit-content'
                                    }}
                                  />
                                  {entry.screenName && (
                                    <Typography variant="caption" fontWeight="medium">
                                      {entry.screenName}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Tooltip title={entry.sessionId}>
                                  <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', cursor: 'help' }}>
                                    {entry.sessionId?.substring(0, 8)}...
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Aucun historique de navigation récent trouvé.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

          </Grid>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={() => setUserDetailsOpen(false)}>
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (loading && users.length === 0) {
    return (
      <AdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Gestion des utilisateurs
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Administration et surveillance des comptes utilisateurs
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Barre de recherche et filtres */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Rechercher par nom ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 190 }}>
                  <InputLabel id="filtre-profil-label">Type de compte</InputLabel>
                  <Select
                    labelId="filtre-profil-label"
                    label="Type de compte"
                    value={filters.accountProfile ?? ''}
                    onChange={(e) => {
                      setPage(0);
                      setFilters({ ...filters, accountProfile: e.target.value || null });
                    }}
                  >
                    <MenuItem value="">
                      Tous ({['enfant', 'anonyme', 'authentifie', 'autre'].reduce(
                        (n, c) => n + (profileCounts[c] || 0),
                        filters.includeBots ? (profileCounts.bot || 0) : 0,
                      )})
                    </MenuItem>
                    {['enfant', 'anonyme', 'authentifie', 'autre'].map((cle) => (
                      <MenuItem key={cle} value={cle}>
                        <Tooltip title={ACCOUNT_PROFILES[cle].aide} placement="right">
                          <span>
                            {ACCOUNT_PROFILES[cle].label}
                            {profileCounts[cle] != null ? ` (${profileCounts[cle]})` : ''}
                          </span>
                        </Tooltip>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      checked={filters.includeBots}
                      onChange={(e) => {
                        setPage(0);
                        setFilters({ ...filters, includeBots: e.target.checked });
                      }}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Inclure les bots
                      {profileCounts.bot != null ? ` (${profileCounts.bot})` : ''}
                    </Typography>
                  }
                />
                <Typography variant="body2" color="text.secondary">
                  {totalUsers} utilisateur(s)
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tableau des utilisateurs */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Utilisateur</TableCell>
                <TableCell>Type / Cycle</TableCell>
                <TableCell>
                  <Tooltip title="Plan réellement appliqué par l'app : max(abonnement propre, plan du parent, pool familial). Le plan propre n'est rappelé que s'il diffère.">
                    <span>Plan effectif</span>
                  </Tooltip>
                </TableCell>
                <TableCell>Niveau</TableCell>
                <TableCell>Dernière connexion</TableCell>
                <TableCell>Combats</TableCell>
                <TableCell>Dernier combat</TableCell>
                <TableCell>Guilde</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Rôle admin</TableCell>
                <TableCell>Onboarding</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.uid} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar src={user.avatar} alt={user.displayName} sx={{ width: 32, height: 32 }}>
                        {user.displayName?.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {user.displayName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Chip
                        label={user.isParentAccount ? "Parent" : "Joueur"}
                        color={user.isParentAccount ? "info" : "default"}
                        size="small"
                        icon={user.isParentAccount ? <GroupIcon /> : <PersonIcon />}
                      />
                      {ACCOUNT_PROFILES[user.accountProfile] && (
                        <Tooltip title={ACCOUNT_PROFILES[user.accountProfile].aide}>
                          <Chip
                            label={
                              user.accountProfile === 'enfant' && user.age != null
                                ? `Enfant · ${user.age} ans`
                                : ACCOUNT_PROFILES[user.accountProfile].label
                            }
                            color={ACCOUNT_PROFILES[user.accountProfile].color}
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      )}
                      {!user.isParentAccount && user.cycle && (
                        <Typography variant="caption" color="text.secondary">
                          Cycle {user.cycle}
                        </Typography>
                      )}
                      {user.accountStatus && user.accountStatus !== 'active' && (
                        <Chip
                          label={user.accountStatus === 'pending_validation' ? "En attente" : user.accountStatus}
                          color="warning"
                          size="small"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{renderPlanChip(user)}</TableCell>
                  <TableCell>{user.level}</TableCell>
                  <TableCell>{formatDate(user.lastLoginDate)}</TableCell>
                  <TableCell>
                    {user.isParentAccount ? (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    ) : (
                      <Typography variant="body2">
                        {user.combatsPlayed || 0}
                        {user.combatsPlayed > 0 && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            ({user.combatsWon || 0}V)
                          </Typography>
                        )}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{user.isParentAccount ? '—' : formatDate(user.lastCombatDate)}</TableCell>
                  <TableCell>
                    {user.guildName ? (
                      <Chip
                        label={user.guildName}
                        size="small"
                        icon={<GroupIcon />}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Aucune
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.isActive ? "Actif" : "Désactivé"}
                      color={user.isActive ? "success" : "error"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {isFullAdmin ? (
                      <Select
                        size="small"
                        value={user.adminRole || (user.isAdmin ? ADMIN_ROLES.ADMIN : '')}
                        displayEmpty
                        onChange={(e) => handleSetAdminRole(user.uid, e.target.value || null)}
                        sx={{ minWidth: 160 }}
                      >
                        <MenuItem value="">
                          <em>Utilisateur</em>
                        </MenuItem>
                        <MenuItem value={ADMIN_ROLES.EXERCISE_EDITOR}>
                          {ADMIN_ROLE_LABELS[ADMIN_ROLES.EXERCISE_EDITOR]}
                        </MenuItem>
                        <MenuItem value={ADMIN_ROLES.ADMIN}>
                          {ADMIN_ROLE_LABELS[ADMIN_ROLES.ADMIN]}
                        </MenuItem>
                      </Select>
                    ) : (
                      <Chip
                        label={getAdminRoleLabel(user)}
                        color={user.adminRole === ADMIN_ROLES.ADMIN ? 'warning' : user.adminRole ? 'info' : 'default'}
                        size="small"
                        icon={user.adminRole ? <AdminIcon /> : <PersonIcon />}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.onboardingCompleted ? "Terminé" : "En cours"}
                      color={user.onboardingCompleted ? "success" : "warning"}
                      size="small"
                      variant={user.onboardingCompleted ? "default" : "outlined"}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Voir les détails">
                        <IconButton
                          size="small"
                          onClick={() => handleViewUserDetails(user)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Historique de navigation">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/admin/users/${user.uid}/history`)}
                          color="info"
                        >
                          <TimelineIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={user.isActive ? "Désactiver" : "Activer"}>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleUserStatus(user.uid, user.isActive)}
                          color={user.isActive ? "error" : "success"}
                        >
                          {user.isActive ? <ToggleOffIcon /> : <ToggleOnIcon />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={totalUsers} // total renvoye par /admin/users
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Lignes par page:"
          labelDisplayedRows={({ from, to }) => `${from}-${to}`}
        />
      </Card>

      <UserDetailsDialog />
    </AdminLayout>
  );
} 